import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, LinearProgress } from "@mui/material";
import PageTemplate from "../Templates/PageTemplate";
import middleware from "../Api/Middleware";
import { useParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import { BorderedDropDown } from "../HouseStyle/Components/DropDowns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faXmark, faPlus, faPen, faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { BackButton, ButtonBlue, ButtonDarkBlue } from "../HouseStyle/Components/Buttons";
import { Modal } from 'react-bootstrap';
import getStatusText from "./Components/statusText";
import ToastSuccess from "../Toasts/ToastSuccess";
import ToastError from "../Toasts/ToastError";
import { BorderedTextInput } from "../HouseStyle/Components/Inputs";
import Modals from "../Products/subpages/sub_components/VariationModals";
import tinycolor from "tinycolor2";

export default function ValidationPage() {
    const { import_id } = useParams();
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
    const [showBrandModal, setShowBrandModal] = useState(false); // New modal state for brands
    const [showAddColorModal, setShowAddColorModal] = useState(false);
    const [showEditColorModal, setShowEditColorModal] = useState(false);
    const [editColor, setEditColor] = useState({name: "", hex: ""});
    const [newColorName, setNewColorName] = useState('');
    const [showSeasonModal, setShowSeasonModal] = useState(false); // New modal state for seasons
    const [editCategory, setEditCategory] = useState(false);
    const [categoryInput, setCategoryInput] = useState("");
    const [editBrand, setEditBrand] = useState(false);
    const [brandInput, setBrandInput] = useState(""); // State for brand input
    const [editSeason, setEditSeason] = useState(false);
    const [seasonInput, setSeasonInput] = useState(""); // State for season input
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [importResults, setImportResults] = useState([]);
    
    const [recordId, setRecordId] = useState(null);
    const [hasCriticalError, setHasCriticalError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [makingAll, setMakingAll] = useState({}); // Track which fields have been processed (disabled permanently after use)
    const [missingFields, setMissingFields] = useState({}); // recordId -> true for fields that must be filled

    const navigate = useNavigate();

        useEffect(() => {
                const css = `
                /* Highlight missing required fields with a blinking red outline */
                .input-missing {
                    animation: tds-blink 1s linear infinite;
                    border: 2px solid rgba(220,53,69,0.9) !important;
                }

                @keyframes tds-blink {
                    0% { box-shadow: 0 0 0px rgba(220,53,69,0.0); }
                    50% { box-shadow: 0 0 12px rgba(220,53,69,0.85); }
                    100% { box-shadow: 0 0 0px rgba(220,53,69,0.0); }
                }

                .InputRow { transition: box-shadow 0.15s ease; }
                `;

                const el = document.createElement('style');
                el.setAttribute('data-inline-validation-style', 'true');
                el.innerHTML = css;
                document.head.appendChild(el);
                return () => { try { document.head.removeChild(el); } catch (e) {} };
        }, []);

    // Helper to extract useful error information from axios/middleware errors
    const getErrorDetails = (error) => {
        const status = error?.response?.status ?? error?.status ?? null;
        const data = error?.response?.data ?? error?.data ?? null;
        const message = (data && (data.message || data.error)) || error?.message || (typeof data === 'string' ? data : JSON.stringify(data)) || 'Onbekende fout';
        return { status, message, data };
    };

    // Fetch validation data
    const fetchAll = useCallback(async () => {
        try {
            const response = await middleware.get(`import/validate?import_id=${import_id}`);
            setImportResults(response.data);

            if (response.data.importStatus !== 5 && response.data.importStatus !== 6) {
                ToastSuccess("Uw aangeleverde data is foutloos gevalideerd", 'import_validated');
                navigate(`/import/resultaat/${import_id}`);
                return;
            }

            setLoading(false);
            setHasCriticalError(false); // Reset error state on success
            return response.data;
        } catch (error) {
            const { status, message } = getErrorDetails(error);

            if (status === 404) {
                // Specific: not found
                ToastError(`Deze import bestaat niet. (404)`, 'invalid_import', 3000);
                setHasCriticalError(true);
                setErrorMessage(`Deze import bestaat niet. (404)`);
                setLoading(false);
                return null;
            }

            // Generic but more informative - this is a critical error
            const errorMsg = `Fout bij ophalen van import (status: ${status ?? 'onbekend'}): ${message}`;
            ToastError(errorMsg, 'fetch_import_error', 5000);
            setHasCriticalError(true);
            setErrorMessage(errorMsg);
            setLoading(false);
            console.error('fetchAll error:', { status, message, error });
            return null;
        }
    }, [import_id, navigate]);

    const unwrapSelectedValue = (value) => {
        if (value && typeof value === "object") {
            if ("value" in value) return value.value;
            if ("color_id" in value) return value.color_id;
            if ("id" in value) return value.id;
            if ("name" in value) return value.name;
        }
        return value;
    };

    // Submit the newly linked columns
    const submitData = async () => {
        const allowedNullFields = ['brand', 'color', 'season', 'category'];
        const missing = {};

        const sourceGroups = (typeof groupedInvalidFields !== 'undefined' && groupedInvalidFields) ? groupedInvalidFields : null;
        if (sourceGroups) {
            for (const [fieldName, issues] of Object.entries(sourceGroups)) {
                if (allowedNullFields.includes(fieldName)) continue;
                for (const issue of issues) {
                    const key = String(issue.recordId);
                    const sel = selectedOptions[key];
                    if (sel === undefined || sel === null || sel === '') {
                        missing[key] = true;
                    }
                }
            }
        } else {
            const invalidClusters = importResults?.validation?.invalid_fields || [];
            for (const cluster of invalidClusters) {
                const fieldName = cluster.fieldName || cluster.field || null;
                if (!fieldName) continue;
                if (allowedNullFields.includes(fieldName)) continue;
                const rows = cluster.rows || [];
                for (const r of rows) {
                    const key = String(r);
                    const sel = selectedOptions[key];
                    if (sel === undefined || sel === null || sel === '') {
                        missing[key] = true;
                    }
                }
            }
        }

        if (Object.keys(missing).length > 0) {
            setMissingFields(missing);
            ToastError('Niet alle verplichte velden zijn ingevuld', 'missing_required_fields');
                // Scroll to and open the first missing field for easier correction
                try {
                    const firstMissingKey = Object.keys(missing)[0];
                    setTimeout(() => {
                        const row = document.querySelector(`[data-record-id="${firstMissingKey}"]`);
                        if (row) {
                            const collapseId = row.getAttribute('data-collapse-id');
                            if (collapseId) {
                                const collapseEl = document.getElementById(collapseId);
                                if (collapseEl && window.bootstrap && window.bootstrap.Collapse) {
                                    const inst = window.bootstrap.Collapse.getOrCreateInstance(collapseEl);
                                    inst.show();
                                } else if (collapseEl) {
                                    collapseEl.classList.add('show');
                                }
                            }
                            try { row.setAttribute('tabindex', '-1'); row.focus(); } catch(e){}
                            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }, 120);
                } catch (e) {
                    console.error('Scroll to missing field failed', e);
                }

                return;
        }

        setMissingFields({});

        const convertedOptions = {};
        for (const [recordId, selectedRaw] of Object.entries(selectedOptions)) {
            const selectedValue = unwrapSelectedValue(selectedRaw);

            const normalized = String(selectedValue).trim().toLowerCase();
            if (["geen merk", "geen kleur", "nvt", "n.v.t."].includes(normalized)) continue;

            let foundText = selectedValue;
            for (const [_, categoryValues] of Object.entries(groupedInvalidFields)) {
                for (const issue of categoryValues) {
                    if (issue.recordId === recordId) {
                        const textKey = Object.entries(issue.validValues).find(([, val]) => val === selectedValue)?.[0];
                        if (textKey) foundText = textKey;
                        break;
                    }
                }
            }
            convertedOptions[recordId] = foundText;
        }

        try {
            const response = await middleware.post(`import/validate?import_id=${import_id}`, {
                selectedOptions: convertedOptions,
            });

            if (response.status === 200) {
                navigate(`/import/resultaat/${import_id}`);
            } else {
                const serverMessage = response?.data?.message || JSON.stringify(response?.data) || 'Geen extra informatie van server';
                ToastError(`Fout bij het verwerken van gekoppelde waarden: ${serverMessage}`, 'submit_validation_error');
            }
        } catch (error) {
            const { status, message } = getErrorDetails(error);
            ToastError(`Netwerk- of serverfout bij verzenden gekoppelde waarden (status: ${status ?? 'onbekend'}): ${message}`, 'submit_validation_network_error');
            console.error('submitData error:', { status, message, error });
        }
        // Convert selectedOptions from IDs back to text values
    };

    // Generic function for creating/updating entities
    const handleEntityAction = async (config) => {
        const {
            recordId,
            endpoint,
            action, // 'create' or 'update'
            data,
            entityName, // e.g., 'categorie', 'merk'
            inputValue,
            modalSetter,
            responseDataPath = null // path to get ID from response for create actions
        } = config;

        try {
            const response = action === 'create' 
                ? await middleware.post(endpoint, data)
                : await middleware.put(endpoint, data);

            if (response.status === 200) {
                await fetchAll();
                
                const actionText = action === 'create' ? 'aangemaakt' : 'bijgewerkt';
                const toastId = action === 'create' ? `added_${entityName}` : `edited_${entityName}`;
                
                ToastSuccess(`${entityName.charAt(0).toUpperCase() + entityName.slice(1)} is succesvol ${actionText}`, toastId);

                // Update selected options for create actions
                if (action === 'create') {
                    const newValue = responseDataPath 
                        ? response.data.data[responseDataPath] 
                        : inputValue;
                        
                    setSelectedOptions((prevSelectedOptions) => ({
                        ...prevSelectedOptions,
                        [recordId]: newValue,
                    }));
                    setMissingFields(prev => { const c = { ...prev }; delete c[String(recordId)]; return c; });
                }
            } else {
                const serverMessage = response?.data?.message || JSON.stringify(response?.data) || 'Geen extra informatie van server';
                const actionText = action === 'create' ? 'aanmaken' : 'bewerken';
                ToastError(`Er is iets fout gegaan bij het ${actionText} van de ${entityName}: ${serverMessage}`);
            }

            modalSetter(false);
        } catch (error) {
            const { status, message } = getErrorDetails(error);
            const actionText = action === 'create' ? 'aanmaken' : 'bewerken';
            ToastError(`Fout bij ${actionText} van ${entityName} (status: ${status ?? 'onbekend'}): ${message}`);
            console.error('handleEntityAction error:', { status, message, error });
        }
    };

    const createNewCategory = async (recordId) => {
        await handleEntityAction({
            recordId,
            endpoint: 'products/category',
            action: 'create',
            data: { title: categoryInput },
            entityName: 'categorie',
            inputValue: categoryInput,
            modalSetter: setShowAddCategoryModal,
            responseDataPath: 'category_id'
        });
    };

    const updateCategory = async (recordId) => {
        await handleEntityAction({
            recordId,
            endpoint: 'products/category',
            action: 'update',
            data: { category_id: selectedOptions[recordId], title: categoryInput },
            entityName: 'categorie',
            modalSetter: setShowAddCategoryModal
        });
    };

    const createNewBrand = async (recordId) => {
        await handleEntityAction({
            recordId,
            endpoint: 'products/brands',
            action: 'create',
            data: { name: brandInput },
            entityName: 'merk',
            inputValue: brandInput,
            modalSetter: setShowBrandModal,
            responseDataPath: 'brand_id'
        });
    };

    const updateBrand = async (recordId) => {
        await handleEntityAction({
            recordId,
            endpoint: 'products/brands',
            action: 'update',
            data: { brand_id: selectedOptions[recordId], name: brandInput },
            entityName: 'merk',
            modalSetter: setShowBrandModal
        });
    };

    const createNewSeason = async (recordId) => {
        await handleEntityAction({
            recordId,
            endpoint: 'products/seasons',
            action: 'create',
            data: { head_season: seasonInput },
            entityName: 'seizoen',
            inputValue: seasonInput,
            modalSetter: setShowSeasonModal,
            responseDataPath: 'season_id'
        });
    };

    const updateSeason = async (recordId) => {
        await handleEntityAction({
            recordId,
            endpoint: 'products/seasons',
            action: 'update',
            data: { season_id: selectedOptions[recordId], head_season: seasonInput },
            entityName: 'seizoen',
            modalSetter: setShowSeasonModal
        });
    };

    // Handle "Make All" functionality - creates new entries for all invalid values in a field
    const handleMakeAll = async (field, issues) => {
        // Prevent multiple clicks while processing
        if (makingAll[field]) {
            return;
        }

        // Mark as processing
        setMakingAll(prev => ({ ...prev, [field]: true }));

        try {
            // Group issues by a case-insensitive normalized fieldValue to avoid creating duplicates
            const normalizedMap = new Map(); // normalized => { displayValue, recordIds: [] }
            issues.forEach(issue => {
                const raw = issue.fieldValue ?? '';
                const trimmed = String(raw).trim();
                const normalized = trimmed.toLowerCase();

                if (!normalizedMap.has(normalized)) {
                    // prefer original casing for display (first occurrence)
                    normalizedMap.set(normalized, { displayValue: trimmed, recordIds: [] });
                }
                normalizedMap.get(normalized).recordIds.push(issue.recordId);
            });

            const fieldDisplayNames = {
                'category': 'categorieën',
                'brand': 'merken',
                'color': 'kleuren',
                'size': 'maten',
                'season': 'seizoenen'
            };
            const displayName = fieldDisplayNames[field] || field;

            // Prepare bulk data for backend using the normalized map's displayValue
            const bulkData = [];
            for (const [normalized, info] of normalizedMap.entries()) {
                if (field === 'category') {
                    bulkData.push({ title: info.displayValue });
                } else if (field === 'brand') {
                    bulkData.push({ name: info.displayValue });
                } else if (field === 'color') {
                    const color = tinycolor(info.displayValue);
                    const hexColor = color.isValid() ? color.toHexString() : null;
                    const obj = { name: info.displayValue };
                    if (hexColor) obj.hex = hexColor;
                    bulkData.push(obj);
                } else if (field === 'size') {
                    bulkData.push({ main: info.displayValue });
                } else if (field === 'season') {
                    bulkData.push({ head_season: info.displayValue });
                }
            }

            if (bulkData.length === 0) {
                ToastError(`Geen geldige ${displayName} gevonden om aan te maken`, `make_all_${field}_empty`, 4000);
                setMakingAll(prev => ({ ...prev, [field]: false }));
                return;
            }

            // Send bulk data to backend
            let response;
            let endpoint;
            if (field === 'category') {
                endpoint = 'products/category';
                response = await middleware.post(endpoint, { bulk_data: bulkData });
            } else if (field === 'brand') {
                endpoint = 'products/brands';
                response = await middleware.post(endpoint, { bulk_data: bulkData });
            } else if (field === 'color') {
                endpoint = 'products/colors';
                response = await middleware.post(endpoint, { bulk_data: bulkData });
            } else if (field === 'size') {
                endpoint = 'products/sizes';
                response = await middleware.post(endpoint, { bulk_data: bulkData });
            } else if (field === 'season') {
                endpoint = 'products/seasons';
                response = await middleware.post(endpoint, { bulk_data: bulkData });
            }

            if (response && response.status === 200) {
                const createdItems = response.data.data || [];
                const mapping = response.data.mapping || {};
                const lowerToId = mapping.lower_to_id || {};

                // helper to sanitize keys for fuzzy matching (remove diacritics, parentheses, punctuation)
                const sanitize = (s = '') => {
                    if (!s) return '';
                    try {
                        return String(s)
                            .normalize('NFD')
                            .replace(/\p{Diacritic}/gu, '')
                            .replace(/\s*\(.*?\)\s*/g, ' ')
                            .replace(/[^a-zA-Z0-9\s\-\/]/g, ' ')
                            .replace(/[\/_]/g, ' ')
                            .replace(/\s+/g, ' ')
                            .trim()
                            .toLowerCase();
                    } catch (e) {
                        return String(s).toLowerCase();
                    }
                };

                // Build sanitized lookup from mapping keys to ids for better matching
                const sanitizedLookup = {};
                Object.entries(lowerToId).forEach(([k, v]) => {
                    const sk = sanitize(k);
                    if (sk) sanitizedLookup[sk] = v;
                });
                if (mapping.original_to_id) {
                    Object.entries(mapping.original_to_id).forEach(([k, v]) => {
                        const sk = sanitize(k);
                        if (sk && !sanitizedLookup[sk]) sanitizedLookup[sk] = v;
                    });
                }

                // If nothing was created and no mapping returned, treat as failure
                if ((!createdItems || createdItems.length === 0) && Object.keys(lowerToId).length === 0) {
                    ToastError(`Geen ${displayName} aangemaakt. Controleer of de waarden geldig zijn.`, `make_all_${field}_no_items`, 5000);
                    setMakingAll(prev => ({ ...prev, [field]: false }));
                    return;
                }

                // Build a map from returned name (normalized) => created item
                const createdMap = new Map();
                createdItems.forEach(item => {
                    const nameKey =
                        field === 'category' ? String(item.title ?? '').trim().toLowerCase() :
                        field === 'brand' ? String(item.name ?? '').trim().toLowerCase() :
                        field === 'color' ? String(item.name ?? '').trim().toLowerCase() :
                        field === 'size' ? String(item.main ?? '').trim().toLowerCase() :
                        field === 'season' ? String(item.head_season ?? '').trim().toLowerCase() : '';
                    if (nameKey) createdMap.set(nameKey, item);
                });

                setSelectedOptions((prevSelectedOptions) => {
                    const updated = { ...prevSelectedOptions };

                    // For each normalized key, assign the returned id to all corresponding recordIds
                    for (const [normalized, info] of normalizedMap.entries()) {
                        let newId = null;

                        const created = createdMap.get(normalized);
                        if (created) {
                            if (field === 'category') newId = created.category_id;
                            else if (field === 'brand') newId = created.brand_id;
                            else if (field === 'color') newId = created.color_id;
                            else if (field === 'size') newId = created.size_id;
                            else if (field === 'season') newId = created.season_id;
                        }

                        if (!newId && mapping.original_to_id) {
                            const orig = info.displayValue || '';
                            // try exact matches using original casing and common variants
                            const origVariants = [orig, String(orig).trim(), String(orig).toLowerCase()];
                            for (const v of origVariants) {
                                if (v && mapping.original_to_id[v]) {
                                    newId = mapping.original_to_id[v];
                                    break;
                                }
                            }
                        }

                        if (!newId && lowerToId && lowerToId[normalized]) {
                            newId = lowerToId[normalized];
                        }

                        if (!newId) {
                            const orig = info.displayValue || '';
                            const s1 = sanitize(normalized);
                            const s2 = sanitize(orig);
                            newId = sanitizedLookup[s1] || sanitizedLookup[s2] || null;
                        }

                        if (newId) {
                            info.recordIds.forEach(recordId => {
                                updated[recordId] = newId;
                            });
                        }
                    }

                    return updated;
                });

                await fetchAll();
                const totalMapped = Object.keys(lowerToId).length || createdItems.length;
                ToastSuccess(`${totalMapped} ${displayName} succesvol aangemaakt en gekoppeld`, `make_all_${field}`, 4000);
                // Reset the loading state after successful creation
                setMakingAll(prev => ({ ...prev, [field]: false }));
            } else {
                const serverMessage = response?.data?.message || response?.data?.error || 'Geen extra informatie beschikbaar van de server';
                ToastError(`Kon ${displayName} niet aanmaken: ${serverMessage}`, `make_all_${field}_error`, 6000);
                setMakingAll(prev => ({ ...prev, [field]: false }));
            }
        } catch (error) {
            const { status, message } = getErrorDetails(error);

            const fieldDisplayNames = {
                'category': 'categorieën',
                'brand': 'merken',
                'color': 'kleuren',
                'size': 'maten',
                'season': 'seizoenen'
            };
            const displayName = fieldDisplayNames[field] || field;

            let errorMsg = `Fout bij het aanmaken van ${displayName}`;
            if (status === 400) {
                errorMsg = `Ongeldige gegevens voor ${displayName}: ${message}`;
            } else if (status === 409) {
                errorMsg = `Sommige ${displayName} bestaan al: ${message}`;
            } else if (status === 500) {
                errorMsg = `Serverfout bij aanmaken van ${displayName}. Probeer het later opnieuw.`;
            } else if (status) {
                errorMsg = `Fout bij aanmaken van ${displayName} (foutcode ${status}): ${message}`;
            } else {
                errorMsg = `Netwerkfout bij aanmaken van ${displayName}: ${message}`;
            }

            ToastError(errorMsg, `make_all_${field}_error`, 7000);
            console.error('handleMakeAll error:', { field, status, message, error });

            // Reset disabled state so user can retry
            setMakingAll(prev => ({ ...prev, [field]: false }));
        }
    };

    // Deletes the current import
    const deleteImport = async () => {
        try {
            const response = await middleware.delete(`import?import_id=${import_id}`);

            if (response.status === 200) {
                navigate(`/import-export-center`);
            } else {
                const serverMessage = response?.data?.message || JSON.stringify(response?.data) || 'Geen extra informatie van server';
                ToastError(`Kon import niet verwijderen: ${serverMessage}`, 'delete_import_error');
            }
        } catch (error) {
            const { status, message } = getErrorDetails(error);
            ToastError(`Fout bij verwijderen import (status: ${status ?? 'onbekend'}): ${message}`, 'delete_import_network_error');
            console.error('deleteImport error:', { status, message, error });
        }
    }

    /**
     * Groups invalid fields by their field names and associates them with valid values.
     */
    const groupInvalidFields = (invalidFields, lookupTables) => {
        const groupedData = {};

        Object.entries(invalidFields).forEach(([recordId, details]) => {
            const field = details.fieldName;
            const fieldValue = details.fieldValue;
            const validValues = lookupTables[details.modelIndex];

            groupedData[field] = groupedData[field] || [];
            groupedData[field].push({ 
                recordId,
                fieldValue,
                count: details.count,
                validValues
            });
        });

        return groupedData;
    };

    useEffect(() => {
        const fetchData = async () => {
            // Fetch data initially
            const response = await fetchAll();
            if (response && response.importStatus === 6) {
                setLoading(false); // Stop loading once status is 6
            }
        };

        fetchData(); // Initial fetch when component mounts

        // Fetch until the status is "Actie verreist" or a critical error occurs
        const interval = setInterval(() => {
            if (hasCriticalError || (importResults && importResults.importStatus === 6)) {
                clearInterval(interval);
            } else {
                fetchAll();
            }
        }, 2000); // Poll every 2 seconds for smooth progress updates

        // Cleanup interval on unmount or when the status is 6 or there's an error
        return () => clearInterval(interval);
    }, [importResults.importStatus, hasCriticalError, fetchAll]); // This now tracks only the importStatus instead of the entire importResults

    if (!importResults || importResults.importStatus !== 6) {
        return (
            <PageTemplate
                navbarTitle={"Importeer validatie"}
                pageIsLoading={loading && !hasCriticalError}
            >
                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
                    <div className="position-absolute" style={{ top: '20px', left: '20px' }}>
                        <BackButton text={'Terug'} onClickFunction={() => navigate('/import-export-center#imports')} />
                    </div>
                    {hasCriticalError ? (
                        <div className="d-flex flex-column align-items-center text-center">
                            <div className="alert alert-danger" role="alert" style={{ maxWidth: '600px' }}>
                                <h4 className="alert-heading">Er is een fout opgetreden</h4>
                                <p className="mb-0">{errorMessage}</p>
                            </div>
                            <p className="text-muted mt-3">Klik op "Terug" om terug te gaan naar het import-export center.</p>
                        </div>
                    ) : (
                        <div className="d-flex flex-column align-items-center text-center" style={{ width: '100%', maxWidth: '600px' }}>
                            <div className="d-flex align-items-center mb-3">
                                <div
                                    className="spinner-border spinner-border-sm ml-auto text-black me-2"
                                    role="status"
                                    aria-hidden="true"
                                ></div>
                                We zijn uw data aan het valideren, sluit dit tabblad niet.
                            </div>
                            <p className="DarkGreyText">(Deze pagina wordt automatisch ververst)</p>
                            <p className="DarkBlueText fw-bold">Huidige status: {getStatusText(importResults.importStatus)}</p>

                            {/* Progress Bar */}
                            {importResults.progress && (
                                <div className="w-100 mt-4">
                                    <div className="d-flex align-items-center gap-3" style={{ minWidth: "300px" }}>
                                        <div>
                                            <h2 className="BlockTitle mb-0">{importResults.progress.percentage?.toFixed(1)}%</h2>
                                            <p className="BeneathFlatText text-muted mb-0">
                                                {importResults.progress.current} / {importResults.progress.total}
                                            </p>
                                        </div>
                                        <div className="flex-grow-1">
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <Box sx={{ width: "100%", mr: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={importResults.progress.percentage || 0}
                                                        sx={{
                                                            height: "8px",
                                                            borderRadius: "12px",
                                                            bgcolor: "rgba(2, 90, 122, 0.12)",
                                                            "& .MuiLinearProgress-bar": {
                                                                background: "linear-gradient(90deg, #009FE3 0%, #025A7A 100%)",
                                                                borderRadius: "12px",
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </div>
                                    </div>
                                    <div className="text-center mt-2 text-muted small">
                                        {importResults.progress.message}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </PageTemplate>
        );
    }

    const groupedInvalidFields = groupInvalidFields(
        importResults.validation.invalid_fields,
        importResults.validation.lookup_tables
    );

    const newColorAdded = async (colorPayload) => {
        const response = await fetchAll();

        // try to get ID from payload
        let selected = colorPayload?.color_id ?? colorPayload?.id ?? colorPayload?.value ?? colorPayload?.name ?? colorPayload;

        // fallback: match name to ID in new lookup
        if (response?.validation && typeof selected === "string") {
            const grouped = groupInvalidFields(
                response.validation.invalid_fields,
                response.validation.lookup_tables
            );

            const issue = grouped?.color?.find(i => i.recordId === recordId);
            if (issue?.validValues) {
                const match = Object.entries(issue.validValues).find(
                    ([name]) => name.toLowerCase() === selected.trim().toLowerCase()
                );
                if (match) selected = match[1];
            }
        }

        setSelectedOptions((prevSelectedOptions) => ({
            ...prevSelectedOptions,
            [recordId]: selected,
        }));
        setMissingFields(prev => { const c = { ...prev }; delete c[String(recordId)]; return c; });
    }

    return (
        <PageTemplate
            navbarTitle={"Importeer validatie"}
            backValue={"/import-export-center"}
            pageIsLoading={loading}
        >
            {/* Delete modal */}
            <Modal
                id="modalBlueprint"
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                
                size="lg"
                centered
            >
                <Modal.Header className="modalTop justify-content-between" style={{ paddingBottom: "0px"}}>
                    <h3>Weet je zeker dat je deze import wilt verwijderen?</h3>
                    <button className=' btn' onClick={() => setShowDeleteModal(false)}>
                        <FontAwesomeIcon icon={faXmark} className="closeModal" />
                    </button>
                </Modal.Header>
                <Modal.Body className="modalCenter">
                </Modal.Body>
                <Modal.Footer className="modalBottom d-flex">
                    <ButtonBlue text={"Import verwijderen"} onClickFunction={deleteImport}/>
                </Modal.Footer>
            </Modal>
            
            {/* Category modal */}
            <Modal
                id="modalBlueprint"
                show={showAddCategoryModal}
                onHide={() => setShowAddCategoryModal(false)}
                
                size="lg"
                centered
            >
                <Modal.Header className="modalTop justify-content-between">
                    <h3>{editCategory ? 'Categorie bewerken' : 'Nieuwe categorie toevoegen'}</h3>
                    <button className='btn' onClick={() => setShowAddCategoryModal(false)}>
                        <FontAwesomeIcon icon={faXmark} className="closeModal" />
                    </button>
                </Modal.Header>
                <Modal.Body className="modalCenter">
                    <BorderedTextInput placeholder='Categorie naam' inputValue={categoryInput} setInputValue={setCategoryInput} size='s'/>
                </Modal.Body>
                <Modal.Footer className="modalBottom d-flex">
                    <ButtonBlue text={editCategory ? 'Bewerken' : "Toevoegen"} onClickFunction={() => editCategory ? updateCategory(recordId) : createNewCategory(recordId)}/>
                </Modal.Footer>
            </Modal>

            {/* Brand modal */}
            <Modal
                id="modalBlueprint"
                show={showBrandModal}
                onHide={() => setShowBrandModal(false)}
                
                size="lg"
                centered
            >
                <Modal.Header className="modalTop justify-content-between">
                    <h3>{editBrand ? `Merk bewerken` : `Nieuw merk toevoegen`}</h3>
                    <button className=' btn' onClick={() => setShowBrandModal(false)}>
                        <FontAwesomeIcon icon={faXmark} className="closeModal" />
                    </button>
                </Modal.Header>
                <Modal.Body className="modalCenter">
                    <BorderedTextInput placeholder='Merk naam' inputValue={brandInput} setInputValue={setBrandInput} size='s'/>
                </Modal.Body>
                <Modal.Footer className="modalBottom d-flex">
                    <ButtonBlue text={editBrand ? 'Bewerken' : 'Toevoegen'} onClickFunction={() => editBrand ? updateBrand(recordId) : createNewBrand(recordId)}/>
                </Modal.Footer>
            </Modal>

            {/* Season modal */}
            <Modal
                id="modalBlueprint"
                show={showSeasonModal}
                onHide={() => setShowSeasonModal(false)}

                size="lg"
                centered
            >
                <Modal.Header className="modalTop justify-content-between">
                    <h3>{editSeason ? `Seizoen bewerken` : `Nieuw seizoen toevoegen`}</h3>
                    <button className=' btn' onClick={() => setShowSeasonModal(false)}>
                        <FontAwesomeIcon icon={faXmark} className="closeModal" />
                    </button>
                </Modal.Header>
                <Modal.Body className="modalCenter">
                    <BorderedTextInput placeholder='Seizoen naam' inputValue={seasonInput} setInputValue={setSeasonInput} size='s'/>
                </Modal.Body>
                <Modal.Footer className="modalBottom d-flex">
                    <ButtonBlue text={editSeason ? 'Bewerken' : 'Toevoegen'} onClickFunction={() => editSeason ? updateSeason(recordId) : createNewSeason(recordId)}/>
                </Modal.Footer>
            </Modal>

            {/* Add color modal */}
            <Modals
                updatedOrAdded={newColorAdded}
                showColorModal={showAddColorModal}
                setShowColorModal={setShowAddColorModal}
                openAddColor={showAddColorModal}
                addNewColor={showAddColorModal}
                setOpenAddColor={value => setShowAddColorModal(value)}
                newColorName={newColorName}
                openEditModal={showEditColorModal}
                editColor={editColor}
                closeEditModal={() => {
                    setShowEditColorModal(false);
                }}
            />

            <div className="import-layout">
                <div className="import-page-header">
                    <BackButton text={'Terug'} onClickFunction={() => navigate('/import-export-center#imports')} />
                    <div className="text-end">
                        <span className="import-section-label d-block">Stap 3 van 3</span>
                        <h5 className="mb-0 text-muted">Validatie</h5>
                    </div>
                </div>

                <div className="validation-card">
                    <div className="validation-summary">
                        <h2>Ongeldige kolommen gedetecteerd</h2>
                        <p>
                            De volgende kolommen zijn ongeldig en moeten worden gekoppeld voordat de import
                            kan worden voortgezet.
                        </p>
                    </div>

                    {/* Accordions with linkable fields */}
                    <div className="accordion validation-accordion d-flex flex-column w-100 mb-3" id="invalidFieldsAccordion">
                    {Object.entries(groupedInvalidFields).map(([field, issues], index) => (
                        <div
                            className="accordion-item"
                            key={field}
                            style={{ width: '100%' }}
                        >
                            {/* Accordion Header */}
                            <h2 className="accordion-header" id={`heading-${index}`}>
                                <button
                                    className={`accordion-button collapsed`}
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#collapse-${index}`}
                                    aria-expanded="false"
                                    aria-controls={`collapse-${index}`}
                                    style={{ width: '100%' }}
                                >
                                    <span><span className="DarkBlueTextBold">{issues.length}</span> {field.charAt(0).toUpperCase() + field.slice(1)}</span>
                                </button>
                            </h2>
                            <div
                                id={`collapse-${index}`}
                                className={`accordion-collapse collapse`}
                                aria-labelledby={`heading-${index}`}
                                data-bs-parent="#invalidFieldsAccordion"
                                style={{ width: '100%' }}
                            >
                                {/* Acordion Body */}
                                <div className="accordion-body" style={{ width: '100%', padding: '0' }}>
                                    {/* Make All Button */}
                                    {(field === 'category' || field === 'brand' || field === 'color' || field === 'size' || field === 'season') && (
                                        <div style={{ padding: '15px', borderBottom: '1px solid #ddd', backgroundColor: '#f8f9fa' }}>
                                            <ButtonBlue
                                                text={
                                                    makingAll[field] ? (
                                                        <>
                                                            <div
                                                                className="spinner-border spinner-border-sm me-2"
                                                                role="status"
                                                                aria-hidden="true"
                                                            ></div>
                                                            Bezig met aanmaken...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
                                                            Maak Alles Aan
                                                        </>
                                                    )
                                                }
                                                onClickFunction={() => handleMakeAll(field, issues)}
                                                disabled={makingAll[field]}
                                            />
                                        </div>
                                    )}
                                    {issues.map((issue, issueIndex) => (
                                        <div
                                            key={issueIndex}
                                            data-record-id={issue.recordId}
                                            data-collapse-id={`collapse-${index}`}
                                            className={`d-flex align-items-center justify-content-between InputRow ${missingFields[String(issue.recordId)] ? 'input-missing' : ''}`}
                                            style={{ borderBottom: issueIndex === issues.length - 1 ? "none" : "1px solid #ddd", padding: "15px" }}
                                        >
                                            {/* Amount */}
                                            <div style={{ flex: 0.1, textAlign: "center" }}>
                                                <span className="DarkGreyText">Aantal: </span> <br />
                                                <strong>{issue.count}</strong>
                                            </div>
                                            {/* Value */}
                                            <div style={{ flex: 1, textAlign: "center" }}>
                                                <span className="DarkGreyText">Ongeldige Waarde: </span> <br />
                                                <strong>{issue.fieldValue}</strong>
                                            </div>
                                            
                                            {/* Arrow */}
                                            <div style={{ flex: 0.1, textAlign: "center" }}>
                                                <FontAwesomeIcon icon={faArrowRight} />
                                            </div>
                                            
                                            {/* Dropdown */}
                                            <div style={{ flex: 1, textAlign: "center", display: "flex", alignItems: "center", gap: "10px" }}>
                                                <BorderedDropDown
                                                    placeholder="Selecteer"
                                                    searchable={true}
                                                    options={Object.entries(issue.validValues)
                                                        .map(([key, value]) => ({
                                                            title: key,
                                                            value: value,
                                                        }))
                                                        .sort((a, b) => a.title.localeCompare(b.title)) // Sort options alphabetically by title
                                                    }
                                                    selectedValue={selectedOptions[issue.recordId]}
                                                    setSelectedValue={(e) => {
                                                        setSelectedOptions({ ...selectedOptions, [issue.recordId]: e });
                                                        setMissingFields(prev => { const c = { ...prev }; delete c[String(issue.recordId)]; return c; });
                                                    }}
                                                />
                                                
                                                {/* Add Category or Add Brand Button */}
                                                {field === "category" && (
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => {
                                                            if (selectedOptions[issue.recordId]) {
                                                                const categoryName = Object.entries(issue.validValues).find(([, val]) => val === selectedOptions[issue.recordId])?.[0];
                                                                setCategoryInput(categoryName);
                                                                setEditCategory(true);
                                                            } else {
                                                                setCategoryInput(issue.fieldValue);
                                                                setEditCategory(false);
                                                            }
                                                            setShowAddCategoryModal(true);
                                                            setRecordId(issue.recordId);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={selectedOptions[issue.recordId] ? faPen : faPlus}/>
                                                    </button>
                                                )}
                                                {field === "brand" && (
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => {
                                                            if (selectedOptions[issue.recordId]) {
                                                                const brandName = Object.entries(issue.validValues).find(([, val]) => val === selectedOptions[issue.recordId])?.[0];
                                                                setBrandInput(brandName);
                                                                setEditBrand(true);
                                                            } else {
                                                                setBrandInput(issue.fieldValue || "");
                                                                setEditBrand(false);
                                                            }
                                                            setShowBrandModal(true);
                                                            setRecordId(issue.recordId);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={selectedOptions[issue.recordId] ? faPen : faPlus}/>
                                                    </button>
                                                )}
                                                {field === "color" && (
                                                    <>
                                                        <button
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={async () => {
                                                                if (selectedOptions[issue.recordId]) {
                                                                    const response = await middleware.get(`products/colors?color_id=${selectedOptions[issue.recordId]}`);
                                                                    setEditColor(response.data.data);
                                                                    setShowEditColorModal(true);
                                                                } else {
                                                                    setShowAddColorModal(true);
                                                                }
                                                                setNewColorName(issue.fieldValue)
                                                                setRecordId(issue.recordId);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={selectedOptions[issue.recordId] ? faPen : faPlus}/>
                                                        </button>
                                                    </>
                                                )}
                                                {field === "season" && (
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => {
                                                            if (selectedOptions[issue.recordId]) {
                                                                const seasonName = Object.entries(issue.validValues).find(([, val]) => val === selectedOptions[issue.recordId])?.[0];
                                                                setSeasonInput(seasonName);
                                                                setEditSeason(true);
                                                            } else {
                                                                setSeasonInput(issue.fieldValue);
                                                                setEditSeason(false);
                                                            }
                                                            setShowSeasonModal(true);
                                                            setRecordId(issue.recordId);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={selectedOptions[issue.recordId] ? faPen : faPlus}/>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Continue button */}
                <div className="d-flex justify-content-start w-100 gap-2">
                    <ButtonDarkBlue text={"Doorgaan met verwerken"} onClickFunction={submitData}/>
                    <ButtonBlue text={"Import verwijderen"} onClickFunction={() => setShowDeleteModal(true)}/>
                </div>
            </div>
        </div>
        </PageTemplate>
    );
}
